import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import { formatFileSize, inferTypeFromName } from '../branchFormUtils';
import { createDecision, fetchDecisionById, mapDecisionFormToPayload, updateDecision, uploadDecisionAttachmentFiles } from '../decisionsApi';
import { draftDecision } from '../aiApi';
import { fetchDecisionTypes } from '../decisionTypesApi';
import { fetchCommittees } from '../committeesApi';
import {
  ATT_TYPES,
  EMPTY_DECISION_FORM,
  attachmentKindLabel,
  getSampleDecisionForm,
  validateDecisionForm,
} from '../decisionFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';
import './DecisionFormPage.css';

function DecisionFormPage({ mode }) {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);
  const [branches, setBranches] = useState([]);
  const [decisionTypes, setDecisionTypes] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [form, setForm] = useState(EMPTY_DECISION_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const fileInputRef = useRef(null);
  const canStepMembers = Number.isFinite(Number(form.totalMembers));
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [aiIdea, setAiIdea] = useState('');
  const [aiDraft, setAiDraft] = useState('');
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraftError, setAiDraftError] = useState('');
  const [aiDraftCopied, setAiDraftCopied] = useState(false);
  const totalAttachments = (form.attachments || []).length;

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t('common.noPerm')); setLoading(false); return; }
    if (invalidEditId) { setLoadError(t('decisions.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const [c, dt, cm] = await Promise.all([fetchBranches(), fetchDecisionTypes(), fetchCommittees()]);
        if (cancelled) return;
        setBranches(c);
        setDecisionTypes(dt);
        setCommittees(cm.filter((x) => x.isActive));
        const firstBranchId = c[0]?.id ? String(c[0].id) : '';
        const firstTypeForBranch = dt.find((x) => String(x.branchId) === firstBranchId)?.id;
        if (isNew) setForm({ ...EMPTY_DECISION_FORM, branchId: firstBranchId });
        else {
          const d = await fetchDecisionById(editId);
          if (cancelled) return;
          setForm({ ...EMPTY_DECISION_FORM, ...d, branchId: String(d.branchId), decisionTypeId: d.decisionTypeId || '', committeeId: d.committeeId || '' });
        }
        if (isNew && firstTypeForBranch) {
          setForm((prev) => ({ ...prev, decisionTypeId: String(firstTypeForBranch) }));
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setErrors((p) => ({ ...p, [e.target.name]: '', _api: '' }));
    setForm((p) => {
      const next = { ...p, [e.target.name]: e.target.value };
      if (e.target.name === 'branchId') {
        const firstType = decisionTypes.find((x) => String(x.branchId) === String(e.target.value));
        next.decisionTypeId = firstType ? String(firstType.id) : '';
        next.committeeId = '';
      }
      return next;
    });
  };

  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setForm((p) => ({
      ...p,
      attachments: [
        ...(p.attachments || []),
        ...files.map((file) => ({
          name: file.name,
          type: inferTypeFromName(file.name),
          size: formatFileSize(file.size, language),
          __file: file,
        })),
      ],
    }));
    e.target.value = '';
  };

  const removeAttachment = (idx) => setForm((p) => ({ ...p, attachments: (p.attachments || []).filter((_, i) => i !== idx) }));

  const filteredDecisionTypes = decisionTypes.filter((x) => String(x.branchId) === String(form.branchId));
  const filteredCommittees = committees.filter((x) => String(x.branchId) === String(form.branchId));

  const handleFillSample = useCallback(() => {
    if (!isNew) return;
    const branchId = form.branchId || (branches[0] && String(branches[0].id)) || '';
    const firstTypeForBranch = decisionTypes.find((x) => String(x.branchId) === String(branchId));
    const currentTypeOk = form.decisionTypeId && decisionTypes.some((x) => String(x.id) === String(form.decisionTypeId) && String(x.branchId) === String(branchId));
    const decisionTypeId = currentTypeOk ? String(form.decisionTypeId) : firstTypeForBranch ? String(firstTypeForBranch.id) : '';
    setErrors({});
    setForm(getSampleDecisionForm({ branchId, decisionTypeId }));
  }, [isNew, form.branchId, form.decisionTypeId, branches, decisionTypes]);

  const handleAiDraft = async () => {
    if (!aiIdea.trim() || aiDraftLoading) return;
    setAiDraftError(''); setAiDraft(''); setAiDraftLoading(true);
    try {
      const result = await draftDecision(aiIdea);
      setAiDraft(result);
    } catch (e) {
      setAiDraftError(e instanceof Error ? e.message : t('decisions.aiDraftError'));
    } finally {
      setAiDraftLoading(false);
    }
  };

  const handleUseAiDraft = () => {
    if (!aiDraft) return;
    setForm((p) => ({ ...p, description: aiDraft }));
    setErrors((p) => ({ ...p, description: '' }));
    setAiDraftOpen(false);
  };

  const handleCopyAiDraft = () => {
    if (!aiDraft) return;
    navigator.clipboard.writeText(aiDraft).then(() => {
      setAiDraftCopied(true);
      setTimeout(() => setAiDraftCopied(false), 2000);
    });
  };

  const handleSave = async () => {
    const errs = validateDecisionForm(form, {
      titleRequired:        t('decisions.valTitle'),
      descriptionRequired:  t('decisions.valDescription'),
      branchRequired:       t('decisions.valBranch'),
      decisionTypeRequired: t('decisions.valDecisionType'),
      deadlineRequired:     t('decisions.valDeadline'),
      totalMembersRequired: t('decisions.valMembers'),
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const pendingFiles = (form.attachments || []).filter((a) => a?.__file).map((a) => a.__file);
      const payload = mapDecisionFormToPayload(form);
      const saved = isNew ? await createDecision(payload) : await updateDecision(editId, payload);
      if (pendingFiles.length > 0) await uploadDecisionAttachmentFiles(saved.id, pendingFiles);
      navigate('/app/decisions');
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('common.errorSave') });
    } finally {
      setSaving(false);
    }
  };

  // AI draft button label
  const aiDraftBtnLabel = useMemo(() => `✨ ${aiDraftOpen ? t('decisions.aiDraftClose') : t('decisions.aiDraftOpen')}`, [aiDraftOpen, t]);

  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {loading && <div className="v2-loading">{t('common.loading')}</div>}

        {!loading && !loadError && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">
                    {isNew ? t('decisions.titleNew') : t('decisions.titleEdit')}
                    <span className="page-title-icon"><Icon name="decisions" size={16} color="#0EA5A0" /></span>
                  </h1>
                </div>
                <div className="action-btns">
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading || !!loadError}>
                    {saving ? t('common.loading') : isNew ? t('decisions.submitNew') : t('decisions.submitEdit')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => navigate('/app/decisions')}>{t('common.cancel')}</button>
                  {isNew && (
                    <button type="button" className="btn btn-outline" onClick={handleFillSample} disabled={saving || loading}>
                      {t('decisions.fillSample')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setAiDraftOpen((v) => !v); setAiDraft(''); setAiDraftError(''); }}
                    style={{ background: aiDraftOpen ? '#0EA5A0' : 'linear-gradient(135deg,#0EA5A0,#14b8a6)', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {aiDraftBtnLabel}
                  </button>
                </div>
              </div>
            </div>
            <div className="card-inner">
              {errors._api && <div className="v2-error">{errors._api}</div>}

              {/* AI Draft Assistant Panel */}
              {aiDraftOpen && (
                <div style={{ marginBottom: 20, border: '1.5px solid #0EA5A0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 12px rgba(14,165,160,.12)' }}>
                  <div style={{ background: 'linear-gradient(90deg,#0EA5A0,#14b8a6)', padding: '11px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '1.15rem' }}>✨</span>
                    <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.97rem' }}>{t('decisions.aiDraftTitle')}</span>
                    <span style={{ color: '#ccfbf1', fontSize: '0.8rem', marginInlineStart: 4 }}>— {t('decisions.aiDraftHint')}</span>
                  </div>
                  <div style={{ padding: '16px 18px', background: '#f8fffe' }}>
                    <label style={{ display: 'block', fontWeight: 700, marginBottom: 7, color: '#1a3c40', fontSize: '0.9rem' }}>{t('decisions.aiDraftIdeaLabel')}</label>
                    <textarea
                      rows={3}
                      value={aiIdea}
                      onChange={(e) => setAiIdea(e.target.value)}
                      placeholder={t('decisions.aiDraftPlaceholder')}
                      style={{ width: '100%', border: '1.5px solid #b2dfdb', borderRadius: 7, padding: '9px 12px', fontSize: '0.92rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#1a3c40', lineHeight: 1.7 }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAiDraft(); }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <button type="button" onClick={handleAiDraft} disabled={aiDraftLoading || !aiIdea.trim()} style={{ background: (!aiIdea.trim() || aiDraftLoading) ? '#b2dfdb' : '#0EA5A0', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', fontWeight: 700, cursor: (!aiIdea.trim() || aiDraftLoading) ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontFamily: 'inherit' }}>
                        {aiDraftLoading ? `⏳ ${t('decisions.aiDraftGenerating')}` : t('decisions.aiDraftGenerate')}
                      </button>
                    </div>
                    {aiDraftError && <div style={{ marginTop: 10, padding: '9px 13px', background: '#ffebee', color: '#c62828', borderRadius: 6, fontSize: '0.88rem' }}>{aiDraftError}</div>}
                    {aiDraft && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ background: '#fff', border: '1px solid #b2dfdb', borderRadius: 8, padding: '13px 16px', whiteSpace: 'pre-wrap', lineHeight: 1.85, color: '#1a3c40', fontSize: '0.91rem', maxHeight: 280, overflowY: 'auto' }}>{aiDraft}</div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                          <button type="button" onClick={handleCopyAiDraft} style={{ background: aiDraftCopied ? '#0EA5A0' : '#f0fffe', color: aiDraftCopied ? '#fff' : '#0EA5A0', border: '1.5px solid #0EA5A0', borderRadius: 6, padding: '6px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit', transition: 'all .2s' }}>
                            {aiDraftCopied ? t('decisions.aiDraftCopied') : t('decisions.aiDraftCopy')}
                          </button>
                          <button type="button" onClick={handleUseAiDraft} style={{ background: '#0EA5A0', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                            ✓ {t('decisions.aiDraftUse')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isNew && (
                <div className="fields-grid" style={{ marginBottom: 16 }}>
                  <div className="field-group span-2">
                    <label className="field-label">{t('decisions.lblCreator')}</label>
                    <div className="field-input"><input value={form.creator || '—'} disabled readOnly dir="auto" /></div>
                  </div>
                </div>
              )}

              <div className="fields-grid">
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblTitle')} <span className="required">*</span></label>
                  <div className="field-input"><input name="title" value={form.title} onChange={handleChange} placeholder={t('decisions.lblTitle')} /></div>
                  {errors.title && <span className="mform__error">{errors.title}</span>}
                </div>
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblBranch')} <span className="required">*</span></label>
                  <div className="field-input select-wrapper"><select name="branchId" value={form.branchId} onChange={handleChange}><option value="">{t('decisions.pickBranch')}</option>{branches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                  {errors.branchId && <span className="mform__error">{errors.branchId}</span>}
                </div>
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblDecisionType')} <span className="required">*</span></label>
                  <div className="field-input select-wrapper"><select name="decisionTypeId" value={form.decisionTypeId} onChange={handleChange}><option value="">{t('decisions.pickDecisionType')}</option>{filteredDecisionTypes.map((dt) => <option key={dt.id} value={dt.id}>{dt.name}</option>)}</select></div>
                  {errors.decisionTypeId && <span className="mform__error">{errors.decisionTypeId}</span>}
                </div>
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblCommittee')}</label>
                  <div className="field-input select-wrapper"><select name="committeeId" value={form.committeeId || ''} onChange={handleChange}><option value="">{t('decisions.pickCommittee')}</option>{filteredCommittees.map((cm) => <option key={cm.id} value={cm.id}>{cm.name}</option>)}</select></div>
                </div>
              </div>

              <div className="section-divider" />

              <div className="fields-grid">
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblPriority')}</label>
                  <div className="field-input select-wrapper"><select name="priority" value={form.priority} onChange={handleChange}><option value="medium">{t('decisions.priorityMedium')}</option><option value="high">{t('decisions.priorityHigh')}</option><option value="low">{t('decisions.priorityLow')}</option></select></div>
                </div>
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblDescription')} <span className="required">*</span></label>
                  <div className="field-input"><input name="description" value={form.description} onChange={handleChange} placeholder={t('decisions.lblDescription')} /></div>
                  {errors.description && <span className="mform__error">{errors.description}</span>}
                </div>
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblDeadline')} <span className="required">*</span></label>
                  <div className="field-input"><input name="deadline" type="date" value={form.deadline} onChange={handleChange} /></div>
                  {errors.deadline && <span className="mform__error">{errors.deadline}</span>}
                </div>
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblTotalMembers')} <span className="required">*</span></label>
                  <div className="number-field">
                    <button type="button" className="step-btn step-minus" disabled={!canStepMembers || Number(form.totalMembers) <= 1} onClick={() => setForm((p) => ({ ...p, totalMembers: String(Math.max(1, Number(p.totalMembers || 1) - 1)) }))}>-</button>
                    <input name="totalMembers" type="number" min={1} value={form.totalMembers} onChange={handleChange} />
                    <button type="button" className="step-btn step-plus" onClick={() => setForm((p) => ({ ...p, totalMembers: String(Math.max(1, Number(p.totalMembers || 1) + 1)) }))}>+</button>
                  </div>
                  {errors.totalMembers && <span className="mform__error">{errors.totalMembers}</span>}
                </div>
              </div>

              <div style={{ marginTop: 20 }} className="fields-grid">
                <div className="field-group span-1">
                  <label className="field-label">{t('decisions.lblStatus')}</label>
                  <div className="field-input select-wrapper"><select name="status" value={form.status} onChange={handleChange}><option value="pending">{t('decisions.statusPending')}</option><option value="approved">{t('decisions.statusApproved')}</option><option value="rejected">{t('decisions.statusRejected')}</option></select></div>
                </div>
              </div>
            </div>

            <div className="attachments-header" onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}>
              <div className="badge">{totalAttachments}</div>
              <span className="attachments-label">{t('decisions.sectionAttachments')}</span>
            </div>

            <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" style={{ display: 'none' }} onChange={onFilesSelected} />

            <div className="upload-zone" onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}>
              <div className="upload-icon"><Icon name="plus" size={20} color="#fff" /></div>
              <div className="upload-title">+ {t('decisions.selectMultipleFiles')}</div>
              <div className="upload-sub">{t('decisions.uploadFooter')}</div>
              <div className="file-types"><span className="file-type-chip">PDF</span><span className="file-type-chip">Word</span><span className="file-type-chip">Excel</span><span className="file-type-chip">PowerPoint</span></div>
            </div>

            {totalAttachments > 0 && (
              <div className="v2-att-list">
                {form.attachments.map((att, i) => (
                  <div key={`${att.name}-${i}`} className="v2-att-item">
                    <span className="v2-att-type">{attachmentKindLabel(att.type || ATT_TYPES[0].value, language)}</span>
                    <span className="v2-att-name">{att.name}</span>
                    <span className="v2-att-size">{att.size || '—'}</span>
                    <button type="button" className="v2-att-remove" onClick={() => removeAttachment(i)}><Icon name="x-circle" size={14} color="#c62828" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DecisionFormPage;
